/*
 * The raw plane: upload, list, preview, delete.
 *
 * The one thing worth getting right here is the 413. Hitting a quota is not a
 * malfunction, and rendering it as "upload failed" tells the reader the wrong thing
 * about what to do next — so the 413's payload is unpacked into the limit that was
 * hit, the value, and the layer that set it, which is the difference between "buy
 * more", "ask an operator" and "delete something".
 *
 * `raw/` is immutable to the agent by kernel enforcement once mounted: the owner
 * writes here, the agent only reads. That asymmetry is the whole reason a source can
 * be trusted as the thing a wiki page cites.
 */
import { useRef, useState, type DragEvent } from 'react'
import { ApiError } from '../../../api/client.ts'
import {
  deleteSource,
  previewSource,
  sourceDownloadUrl,
  uploadSources,
  type KnowledgeBaseLimits,
  type SourceFile,
  type SourceList,
  type SourcePreview,
} from '../../../api/knowledgeBases.ts'
import { Empty, Loaded } from '../components/AsyncState.tsx'
import { bytes, num } from '../lib/format.ts'
import { sourceLook } from '../lib/limits.ts'
import type { Resource } from '../lib/useResource.ts'

interface FilesTabProps {
  kbId: string
  archived: boolean
  sources: Resource<SourceList>
  limits: Resource<KnowledgeBaseLimits>
  onChanged: () => void
}

interface QuotaProblem {
  message: string
  field: string | null
  limit: number | null
  current: number | null
  source: string | null
}

export function FilesTab({ kbId, archived, sources, limits, onChanged }: FilesTabProps) {
  const [busy, setBusy] = useState(false)
  const [quota, setQuota] = useState<QuotaProblem | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [over, setOver] = useState(false)
  const [selected, setSelected] = useState<string | null>(null)
  const [preview, setPreview] = useState<SourcePreview | null>(null)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const input = useRef<HTMLInputElement>(null)

  const perFile = limits.data?.limits?.kb_max_source_file_bytes
  const filesLimit = limits.data?.limits?.kb_max_raw_files
  const slotsLeft =
    filesLimit?.value !== null && filesLimit?.value !== undefined
      ? Math.max(0, filesLimit.value - (limits.data?.usage.raw_files ?? 0))
      : null

  async function upload(files: File[]) {
    if (files.length === 0) return
    setBusy(true)
    setQuota(null)
    setError(null)
    try {
      // One file per request: the ingress transport cap is sized for a single
      // file at the per-file entitlement plus the multipart envelope, so a
      // batch must never share one request — it would hit the transport limit
      // and be refused with a bare 413 instead of the API's own answer. A
      // failure stops the batch and names its file; what uploaded stays.
      for (const file of files) {
        try {
          await uploadSources(kbId, [file])
        } catch (err) {
          if (err instanceof ApiError && err.status === 413) setQuota(quotaProblem(err))
          else setError(`${file.name}: ${uploadFailure(err)}`)
          break
        }
      }
      onChanged()
      sources.reload()
      limits.reload()
    } finally {
      setBusy(false)
      if (input.current) input.current.value = ''
    }
  }

  async function remove(path: string) {
    setBusy(true)
    setError(null)
    try {
      await deleteSource(kbId, path)
      if (selected === path) {
        setSelected(null)
        setPreview(null)
      }
      onChanged()
      sources.reload()
      limits.reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'не удалось удалить файл')
    } finally {
      setBusy(false)
    }
  }

  async function open(path: string) {
    setSelected(path)
    setPreview(null)
    setPreviewError(null)
    try {
      setPreview(await previewSource(kbId, path))
    } catch (err) {
      setPreviewError(err instanceof Error ? err.message : 'не удалось открыть файл')
    }
  }

  function onDrop(event: DragEvent) {
    event.preventDefault()
    setOver(false)
    if (archived) return
    void upload([...event.dataTransfer.files])
  }

  return (
    <div className="pane">
      <div className="two-col">
        <div className="card">
          <div className="card__head">
            <h3>
              Источники <span className="path">raw/</span>
            </h3>
            <div className="right">
              <span className="pill pill--neutral pill--sm" title="Кернел не даёт агенту писать сюда">
                агент: только чтение
              </span>
              <span className="pill pill--info pill--sm">
                {num(sources.data?.total ?? 0)} файл.
              </span>
            </div>
          </div>

          {archived ? (
            <div className="limit-hit" style={{ borderColor: 'var(--h2)', background: 'none' }}>
              База в архиве — загрузка и удаление отключены.
            </div>
          ) : (
            <>
              <input
                ref={input}
                type="file"
                multiple
                hidden
                onChange={(event) => void upload([...(event.target.files ?? [])])}
              />
              <button
                type="button"
                className="dropzone"
                data-over={over}
                disabled={busy}
                onClick={() => input.current?.click()}
                onDragOver={(event) => {
                  event.preventDefault()
                  setOver(true)
                }}
                onDragLeave={() => setOver(false)}
                onDrop={onDrop}
              >
                {busy
                  ? 'Загружаем…'
                  : 'Перетащите файлы или нажмите'}
                <br />
                <span style={{ fontSize: 11 }}>
                  {perFile?.value
                    ? `до ${bytes(perFile.value)} на файл`
                    : 'размер файла не ограничен планом'}
                  {slotsLeft !== null ? ` · осталось ${num(slotsLeft)} слотов` : ''}
                </span>
              </button>
            </>
          )}

          {quota ? <QuotaNotice problem={quota} onDismiss={() => setQuota(null)} /> : null}

          {error ? (
            <div className="limit-hit" style={{ borderColor: 'rgba(248,113,113,.34)' }}>
              <div className="limit-hit__title" style={{ color: 'var(--er-tx)' }}>
                Не удалось выполнить операцию
              </div>
              {error}
            </div>
          ) : null}

          <Loaded resource={sources} what="список источников" skeletonRows={5}>
            {(data) =>
              (data.files ?? []).length === 0 ? (
                <Empty title="В raw/ пока пусто">
                  Загрузите файлы — агент прочитает их и напишет по ним страницы wiki. Сами
                  источники он изменить не может.
                </Empty>
              ) : (
                <div className="flist">
                  {(data.files ?? []).map((file) => (
                    <FileRow
                      key={file.path}
                      file={file}
                      selected={selected === file.path}
                      archived={archived}
                      busy={busy}
                      onOpen={() => void open(file.path)}
                      onDelete={() => void remove(file.path)}
                    />
                  ))}
                </div>
              )
            }
          </Loaded>
        </div>

        <div className="card">
          <div className="card__head">
            <h3>
              Просмотр{' '}
              {selected ? <span className="path">{selected}</span> : null}
            </h3>
            <div className="right">
              {selected ? (
                <a
                  className="btn btn--ghost btn--sm"
                  href={sourceDownloadUrl(kbId, selected)}
                  download
                >
                  Скачать
                </a>
              ) : null}
            </div>
          </div>

          {selected === null ? (
            <Empty title="Файл не выбран">Нажмите на имя файла слева.</Empty>
          ) : previewError ? (
            <div className="state state--error">
              <div className="state__title">Не удалось открыть файл</div>
              <div className="state__detail">{previewError}</div>
            </div>
          ) : preview === null ? (
            <div className="state">
              <span className="skeleton" style={{ width: '80%' }} />
            </div>
          ) : preview.kind === 'text' && preview.text !== null ? (
            <pre className="editor">{preview.text}</pre>
          ) : preview.kind === 'image' ? (
            <div style={{ padding: 15 }}>
              <img
                src={sourceDownloadUrl(kbId, selected)}
                alt={preview.filename}
                style={{ maxWidth: '100%', borderRadius: 8, display: 'block' }}
              />
            </div>
          ) : (
            <Empty title="Предпросмотр недоступен">
              {preview.contentType || 'бинарный файл'} — файл можно скачать целиком.
              {preview.kind === 'text' ? ' Текст слишком велик для встроенного предпросмотра.' : ''}
            </Empty>
          )}
        </div>
      </div>
    </div>
  )
}

function FileRow({
  file,
  selected,
  archived,
  busy,
  onOpen,
  onDelete,
}: {
  file: SourceFile
  selected: boolean
  archived: boolean
  busy: boolean
  onOpen: () => void
  onDelete: () => void
}) {
  return (
    <div className="frow" style={selected ? { background: 'var(--ac-tint-soft)' } : undefined}>
      <span className="frow__ic" aria-hidden="true">
        {file.content_type.startsWith('image/') ? '◫' : '▤'}
      </span>
      <button type="button" className="frow__name" onClick={onOpen}>
        {file.path}
      </button>
      <span className="frow__size">{bytes(file.size_bytes)}</span>
      {archived ? null : (
        <button
          type="button"
          className="frow__del"
          disabled={busy}
          title={`Удалить ${file.path}`}
          aria-label={`Удалить ${file.path}`}
          onClick={onDelete}
        >
          ✕
        </button>
      )}
    </div>
  )
}

function QuotaNotice({
  problem,
  onDismiss,
}: {
  problem: QuotaProblem
  onDismiss: () => void
}) {
  const look = problem.source ? sourceLook(problem.source) : null
  return (
    <div className="limit-hit">
      <div className="limit-hit__title">Вы упёрлись в лимит</div>
      {problem.message}
      <div className="limit-hit__meta">
        {problem.limit !== null ? <span>предел: {num(problem.limit)}</span> : null}
        {problem.current !== null ? <span>сейчас: {num(problem.current)}</span> : null}
        {look ? (
          <>
            <span className={`src-tag ${look.className}`}>{look.label}</span>
            <span>{look.recourse}</span>
          </>
        ) : null}
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          style={{ marginLeft: 'auto' }}
          onClick={onDismiss}
        >
          Понятно
        </button>
      </div>
    </div>
  )
}

/**
 * Unpack the 413 the raw plane returns.
 *
 * It names the field, the value, the current usage and the layer that set it —
 * everything needed to say what to do about it, so none of that is thrown away in
 * favour of a generic message.
 */
function quotaProblem(err: ApiError): QuotaProblem {
  const detail = err.detail() ?? {}
  const message =
    typeof detail.message === 'string' && detail.message.length > 0
      ? detail.message
      : 'загрузка превысила один из лимитов базы'
  return {
    message,
    field: typeof detail.field === 'string' ? detail.field : null,
    limit: typeof detail.limit === 'number' ? detail.limit : null,
    current: typeof detail.current === 'number' ? detail.current : null,
    source: typeof detail.source === 'string' ? detail.source : null,
  }
}

function uploadFailure(err: unknown): string {
  if (!(err instanceof ApiError)) return err instanceof Error ? err.message : 'ошибка загрузки'
  switch (err.status) {
    case 400:
      return 'Недопустимое имя файла или путь вне плоскости raw/.'
    case 409:
      return 'База в архиве — запись отключена.'
    case 503:
      return 'Архив источников не удалось опубликовать. Ничего не сохранено, попробуйте ещё раз.'
    default:
      return err.message
  }
}
